// Typed operations on a Creative Document (CREATIVE-MODEL-V1.md §15.1).
// One language for the visual editor (undo/redo = inverse operations) and
// for AI modifications (ADR-0018 §2.1-6):
//
//   operations (validated) → deterministic application (pure, atomic)
//   → the SAME validateCreativeDocument → accepted document | issues
//
// Application never mutates its input and never "fixes" anything: an
// operation that cannot apply fails the whole batch with structured issues.

import {
  assetRef,
  customEventDecl,
  element,
  fallbackSpec,
  fontRef,
  formDecl,
  gameDecl,
  layout,
  layoutOverride,
  link,
  meta,
  rule,
  scene,
  surveyInstrument,
  timerDecl,
  variableDecl,
  type CreativeDocument,
} from "./schema/document.js";
import {
  FAIL,
  array,
  id,
  integer,
  isPlainObject,
  literal,
  object,
  oneOf,
  optional,
  union,
  type Infer,
  type Schema,
} from "./schema/dsl.js";
import { IssueSink, sortIssues, type ValidationIssue } from "./issues.js";
import { validateCreativeDocument, type ValidateOptions, type ValidationResult } from "./validate/index.js";

type JsonRecord = Record<string, unknown>;

const patch: Schema<JsonRecord> = {
  describe: () => ({
    type: "object",
    description:
      "top-level fields to replace on the target (id, type and kind cannot change); the result is validated as a whole",
  }),
  parse(value, path, sink) {
    if (!isPlainObject(value)) {
      sink.add("INVALID_TYPE", path, "Expected object.", { expected: "object" });
      return FAIL;
    }
    for (const key of ["id", "type", "kind"])
      if (Object.hasOwn(value, key)) {
        sink.add(
          "INVALID_VALUE",
          `${path}.${key}`,
          "Identity fields cannot be changed by an update; remove and add instead.",
          {}
        );
        return FAIL;
      }
    return value;
  },
};

const parentTarget = union("in", {
  scene: object({ in: literal("scene"), id: id() }),
  group: object({ in: literal("group"), id: id() }),
  slot: object({ in: literal("slot"), id: id(), slot: id() }),
  overlay: object({ in: literal("overlay"), side: oneOf(["below", "above"]) }),
});
type ParentTarget = Infer<typeof parentTarget>;
const position = optional(integer({ min: 0, max: 10_000 }));

export const creativeOperation = union("op", {
  addElement: object({ op: literal("addElement"), element, parent: parentTarget, index: position }),
  updateElement: object({
    op: literal("updateElement"),
    id: id(),
    set: patch,
    unset: optional(array(id(), { max: 32 })),
  }),
  removeElement: object({ op: literal("removeElement"), id: id() }),
  moveElement: object({ op: literal("moveElement"), id: id(), parent: parentTarget, index: position }),
  addScene: object({ op: literal("addScene"), scene, index: position }),
  updateScene: object({
    op: literal("updateScene"),
    id: id(),
    set: patch,
    unset: optional(array(id(), { max: 16 })),
  }),
  removeScene: object({ op: literal("removeScene"), id: id() }),
  /** Moves a scene to `index` of the scene list (order = scene.next/prev order and editor order). */
  moveScene: object({ op: literal("moveScene"), id: id(), index: integer({ min: 0, max: 10_000 }) }),
  /** Sets the scene the creative starts in. */
  setInitialScene: object({ op: literal("setInitialScene"), id: id() }),
  addLayout: object({ op: literal("addLayout"), layout }),
  setLayoutOverride: object({
    op: literal("setLayoutOverride"),
    layout: id(),
    element: id(),
    override: optional(layoutOverride),
  }),
  addRule: object({ op: literal("addRule"), rule, index: position }),
  replaceRule: object({ op: literal("replaceRule"), rule }),
  removeRule: object({ op: literal("removeRule"), id: id() }),
  removeLayout: object({ op: literal("removeLayout"), id: id() }),
  setLink: object({ op: literal("setLink"), link }),
  removeLink: object({ op: literal("removeLink"), id: id() }),
  pinAsset: object({ op: literal("pinAsset"), asset: assetRef }),
  unpinAsset: object({ op: literal("unpinAsset"), key: id() }),
  setFont: object({ op: literal("setFont"), font: fontRef }),
  removeFont: object({ op: literal("removeFont"), key: id() }),
  setVariable: object({ op: literal("setVariable"), variable: variableDecl }),
  setTimer: object({ op: literal("setTimer"), timer: timerDecl }),
  removeVariable: object({ op: literal("removeVariable"), id: id() }),
  removeTimer: object({ op: literal("removeTimer"), id: id() }),
  /** Declares (or replaces, by name) a custom event; only declared events can be emitted. */
  setEvent: object({ op: literal("setEvent"), event: customEventDecl }),
  removeEvent: object({ op: literal("removeEvent"), name: id() }),
  /** Replaces the document metadata (title, locale, accessibility label, intended product). */
  setMeta: object({ op: literal("setMeta"), meta }),
  /** Sets the static fallback block; omitted = no fallback yet (a DRAFT may be saved without one). */
  setFallback: object({ op: literal("setFallback"), fallback: optional(fallbackSpec) }),
  /**
   * Replaces the survey instrument as a whole (7.6E); omitted = the document has no survey.
   * The graph is data: the validator proves acyclic / reachable / ≤ 8 per path after applying.
   */
  setSurvey: object({ op: literal("setSurvey"), survey: optional(surveyInstrument) }),
  /** Declares (or replaces, by id) a non-PII form (7.6E). */
  setForm: object({ op: literal("setForm"), form: formDecl }),
  /** Removes a form declaration; `field` elements that present it must be removed in the same batch. */
  removeForm: object({ op: literal("removeForm"), id: id() }),
  /**
   * Replaces the declared game as a whole (7.6F); omitted = the document has no game module.
   * The validator proves roles, pairs, drops, win condition and results after applying.
   */
  setGame: object({ op: literal("setGame"), game: optional(gameDecl) }),
});
export type CreativeOperation = Infer<typeof creativeOperation>;

export const MAX_OPERATIONS_PER_BATCH = 200;

type MutableDoc = JsonRecord & {
  meta: JsonRecord;
  fonts?: JsonRecord[];
  fallback?: { perLayout: Record<string, JsonRecord>; linkId: string; alt: string };
  scenes: JsonRecord[];
  elements: JsonRecord[];
  layouts: JsonRecord[];
  rules?: JsonRecord[];
  links: JsonRecord[];
  assets: JsonRecord[];
  variables?: JsonRecord[];
  timers?: JsonRecord[];
  events?: JsonRecord[];
  forms?: JsonRecord[];
  survey?: JsonRecord;
  game?: JsonRecord;
  initialSceneId: string;
  overlay?: { below: string[]; above: string[] };
};

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const insertAt = <T>(list: T[], item: T, index: number | undefined) => {
  list.splice(index === undefined ? list.length : Math.min(index, list.length), 0, item);
};
/** Replaces or adds keys without ever writing to a prototype (fromEntries defines own properties). */
const merged = (base: JsonRecord, set: JsonRecord, unset: readonly string[] = []): JsonRecord =>
  Object.fromEntries([...Object.entries(base).filter(([k]) => !unset.includes(k)), ...Object.entries(set)]);

function containers(doc: MutableDoc): string[][] {
  const lists: string[][] = [];
  for (const s of doc.scenes) {
    lists.push(s.children as string[]);
    for (const ids of Object.values((s.childrenByLayout ?? {}) as Record<string, string[]>)) lists.push(ids);
  }
  if (doc.overlay) lists.push(doc.overlay.below, doc.overlay.above);
  for (const el of doc.elements) {
    if (el.type === "group") lists.push(el.children as string[]);
    if (el.type === "component")
      for (const ids of Object.values((el.slots ?? {}) as Record<string, string[]>)) lists.push(ids);
  }
  return lists;
}

function detach(doc: MutableDoc, elementId: string) {
  for (const list of containers(doc)) {
    for (let i = list.length - 1; i >= 0; i--) if (list[i] === elementId) list.splice(i, 1);
  }
}

function resolveParent(doc: MutableDoc, target: ParentTarget): string[] | undefined {
  switch (target.in) {
    case "scene":
      return doc.scenes.find((s) => s.id === target.id)?.children as string[] | undefined;
    case "group": {
      const g = doc.elements.find((e) => e.id === target.id && e.type === "group");
      return g?.children as string[] | undefined;
    }
    case "slot": {
      const c = doc.elements.find((e) => e.id === target.id && e.type === "component");
      if (!c) return undefined;
      const slots = (c.slots ?? {}) as Record<string, string[]>;
      if (!Object.hasOwn(slots, target.slot)) c.slots = merged(slots, { [target.slot]: [] });
      return (c.slots as Record<string, string[]>)[target.slot];
    }
    case "overlay":
      doc.overlay ??= { below: [], above: [] };
      return doc.overlay[target.side];
  }
}

function descendants(doc: MutableDoc, rootId: string): string[] {
  const out: string[] = [];
  const visit = (elementId: string) => {
    if (out.includes(elementId)) return;
    out.push(elementId);
    const el = doc.elements.find((e) => e.id === elementId);
    if (el?.type === "group") (el.children as string[]).forEach(visit);
    if (el?.type === "component")
      Object.values((el.slots ?? {}) as Record<string, string[]>).forEach((ids) => {
        ids.forEach(visit);
      });
  };
  visit(rootId);
  return out;
}

function removeElements(doc: MutableDoc, ids: readonly string[]) {
  for (const elementId of ids) detach(doc, elementId);
  doc.elements = doc.elements.filter((e) => !ids.includes(e.id as string));
  for (const l of doc.layouts) {
    const overrides = l.overrides as JsonRecord | undefined;
    if (overrides) l.overrides = Object.fromEntries(Object.entries(overrides).filter(([k]) => !ids.includes(k)));
  }
}

function upsert(list: JsonRecord[], item: JsonRecord, key: string) {
  const i = list.findIndex((x) => x[key] === item[key]);
  if (i >= 0) list[i] = item;
  else list.push(item);
}

function applyOne(doc: MutableDoc, op: CreativeOperation, path: string, sink: IssueSink): void {
  const missing = (field: string, kind: string, value: string) => {
    sink.add("UNKNOWN_REFERENCE", `${path}.${field}`, `Unknown ${kind} "${value}".`, { kind, id: value });
  };
  const findElement = (elementId: string) => doc.elements.find((e) => e.id === elementId);
  switch (op.op) {
    case "addElement": {
      if (findElement(op.element.id)) {
        sink.add("DUPLICATE_ID", `${path}.element.id`, `Element "${op.element.id}" already exists.`, {
          id: op.element.id,
        });
        return;
      }
      const list = resolveParent(doc, op.parent);
      if (!list) {
        missing("parent", op.parent.in, "id" in op.parent ? op.parent.id : op.parent.side);
        return;
      }
      doc.elements.push(clone(op.element));
      insertAt(list, op.element.id, op.index);
      return;
    }
    case "updateElement": {
      const i = doc.elements.findIndex((e) => e.id === op.id);
      const current = doc.elements[i];
      if (!current) {
        missing("id", "element", op.id);
        return;
      }
      doc.elements[i] = merged(current, clone(op.set), op.unset);
      return;
    }
    case "removeElement":
      if (!findElement(op.id)) {
        missing("id", "element", op.id);
        return;
      }
      removeElements(doc, descendants(doc, op.id));
      return;
    case "moveElement": {
      if (!findElement(op.id)) {
        missing("id", "element", op.id);
        return;
      }
      if (op.parent.in !== "scene" && op.parent.in !== "overlay" && descendants(doc, op.id).includes(op.parent.id)) {
        sink.add("PARENT_CYCLE", `${path}.parent`, "An element cannot be moved into its own subtree.", {});
        return;
      }
      detach(doc, op.id);
      const list = resolveParent(doc, op.parent);
      if (!list) {
        missing("parent", op.parent.in, "id" in op.parent ? op.parent.id : op.parent.side);
        return;
      }
      insertAt(list, op.id, op.index);
      return;
    }
    case "addScene":
      if (doc.scenes.some((s) => s.id === op.scene.id)) {
        sink.add("DUPLICATE_ID", `${path}.scene.id`, `Scene "${op.scene.id}" already exists.`, { id: op.scene.id });
        return;
      }
      insertAt(doc.scenes, clone(op.scene) as unknown as JsonRecord, op.index);
      return;
    case "updateScene": {
      const i = doc.scenes.findIndex((s) => s.id === op.id);
      const current = doc.scenes[i];
      if (!current) {
        missing("id", "scene", op.id);
        return;
      }
      doc.scenes[i] = merged(current, clone(op.set), op.unset);
      return;
    }
    case "moveScene": {
      const from = doc.scenes.findIndex((x) => x.id === op.id);
      const moving = doc.scenes[from];
      if (!moving) {
        missing("id", "scene", op.id);
        return;
      }
      doc.scenes.splice(from, 1);
      insertAt(doc.scenes, moving, op.index);
      return;
    }
    case "setInitialScene":
      if (!doc.scenes.some((x) => x.id === op.id)) {
        missing("id", "scene", op.id);
        return;
      }
      doc.initialSceneId = op.id;
      return;
    case "removeScene": {
      const s = doc.scenes.find((x) => x.id === op.id);
      if (!s) {
        missing("id", "scene", op.id);
        return;
      }
      removeElements(
        doc,
        (s.children as string[]).flatMap((c) => descendants(doc, c))
      );
      doc.scenes = doc.scenes.filter((x) => x.id !== op.id);
      return;
    }
    case "addLayout":
      if (doc.layouts.some((l) => l.id === op.layout.id)) {
        sink.add("DUPLICATE_ID", `${path}.layout.id`, `Layout "${op.layout.id}" already exists.`, { id: op.layout.id });
        return;
      }
      doc.layouts.push(clone(op.layout));
      return;
    case "setLayoutOverride": {
      const l = doc.layouts.find((x) => x.id === op.layout);
      if (!l) {
        missing("layout", "layout", op.layout);
        return;
      }
      if (!findElement(op.element)) {
        missing("element", "element", op.element);
        return;
      }
      const overrides = (l.overrides ?? {}) as JsonRecord;
      const rest = Object.fromEntries(Object.entries(overrides).filter(([k]) => k !== op.element));
      l.overrides = op.override ? merged(rest, { [op.element]: clone(op.override) }) : rest;
      return;
    }
    case "addRule":
      doc.rules ??= [];
      if (doc.rules.some((r) => r.id === op.rule.id)) {
        sink.add("DUPLICATE_ID", `${path}.rule.id`, `Rule "${op.rule.id}" already exists.`, { id: op.rule.id });
        return;
      }
      insertAt(doc.rules, clone(op.rule) as unknown as JsonRecord, op.index);
      return;
    case "replaceRule": {
      const i = (doc.rules ?? []).findIndex((r) => r.id === op.rule.id);
      if (i < 0 || !doc.rules) {
        missing("rule.id", "rule", op.rule.id);
        return;
      }
      doc.rules[i] = clone(op.rule);
      return;
    }
    case "removeRule":
      if (!(doc.rules ?? []).some((r) => r.id === op.id)) {
        missing("id", "rule", op.id);
        return;
      }
      doc.rules = (doc.rules ?? []).filter((r) => r.id !== op.id);
      return;
    case "removeLayout": {
      if (!doc.layouts.some((l) => l.id === op.id)) {
        missing("id", "layout", op.id);
        return;
      }
      doc.layouts = doc.layouts.filter((l) => l.id !== op.id);
      // Elements that existed ONLY in this variant go with it; the others stop listing it.
      const onlyHere: string[] = [];
      doc.elements = doc.elements.map((el) => {
        const onlyIn = el.onlyIn as string[] | undefined;
        if (!onlyIn?.includes(op.id)) return el;
        const rest = onlyIn.filter((l) => l !== op.id);
        if (rest.length === 0) onlyHere.push(el.id as string);
        return rest.length === 0 ? el : merged(el, { onlyIn: rest });
      });
      removeElements(
        doc,
        onlyHere.flatMap((e) => descendants(doc, e))
      );
      doc.scenes = doc.scenes.map((sc) => {
        const byLayout = sc.childrenByLayout as Record<string, string[]> | undefined;
        if (!byLayout || !Object.hasOwn(byLayout, op.id)) return sc;
        const rest = Object.fromEntries(Object.entries(byLayout).filter(([k]) => k !== op.id));
        return Object.keys(rest).length === 0
          ? merged(sc, {}, ["childrenByLayout"])
          : merged(sc, { childrenByLayout: rest });
      });
      if (doc.fallback && Object.hasOwn(doc.fallback.perLayout, op.id)) {
        const perLayout = Object.fromEntries(Object.entries(doc.fallback.perLayout).filter(([k]) => k !== op.id));
        // A fallback block needs at least one variant entry: removing the last one removes the block.
        if (Object.keys(perLayout).length === 0) delete doc.fallback;
        else doc.fallback = { ...doc.fallback, perLayout };
      }
      return;
    }
    case "setLink":
      upsert(doc.links, clone(op.link), "id");
      return;
    case "removeLink":
      if (!doc.links.some((l) => l.id === op.id)) {
        missing("id", "link", op.id);
        return;
      }
      doc.links = doc.links.filter((l) => l.id !== op.id);
      return;
    case "pinAsset":
      upsert(doc.assets, clone(op.asset), "key");
      return;
    case "unpinAsset":
      if (!doc.assets.some((a) => a.key === op.key)) {
        missing("key", "asset", op.key);
        return;
      }
      doc.assets = doc.assets.filter((a) => a.key !== op.key);
      return;
    case "setFont":
      doc.fonts ??= [];
      upsert(doc.fonts, clone(op.font), "key");
      return;
    case "removeFont":
      if (!(doc.fonts ?? []).some((f) => f.key === op.key)) {
        missing("key", "font", op.key);
        return;
      }
      doc.fonts = (doc.fonts ?? []).filter((f) => f.key !== op.key);
      return;
    case "setMeta":
      doc.meta = clone(op.meta);
      return;
    case "setFallback":
      if (op.fallback) doc.fallback = clone(op.fallback);
      else delete doc.fallback;
      return;
    case "setVariable":
      doc.variables ??= [];
      upsert(doc.variables, clone(op.variable), "id");
      return;
    case "setTimer":
      doc.timers ??= [];
      upsert(doc.timers, clone(op.timer), "id");
      return;
    case "removeVariable":
      if (!(doc.variables ?? []).some((v) => v.id === op.id)) {
        missing("id", "variable", op.id);
        return;
      }
      doc.variables = (doc.variables ?? []).filter((v) => v.id !== op.id);
      return;
    case "removeTimer":
      if (!(doc.timers ?? []).some((t) => t.id === op.id)) {
        missing("id", "timer", op.id);
        return;
      }
      doc.timers = (doc.timers ?? []).filter((t) => t.id !== op.id);
      return;
    case "setEvent":
      doc.events ??= [];
      upsert(doc.events, clone(op.event), "name");
      return;
    case "removeEvent":
      if (!(doc.events ?? []).some((e) => e.name === op.name)) {
        missing("name", "event", op.name);
        return;
      }
      doc.events = (doc.events ?? []).filter((e) => e.name !== op.name);
      return;
    case "setSurvey":
      if (op.survey) doc.survey = clone(op.survey);
      else delete doc.survey;
      return;
    case "setGame":
      if (op.game) doc.game = clone(op.game);
      else delete doc.game;
      return;
    case "setForm":
      doc.forms ??= [];
      upsert(doc.forms, clone(op.form), "id");
      return;
    case "removeForm":
      if (!(doc.forms ?? []).some((f) => f.id === op.id)) {
        missing("id", "form", op.id);
        return;
      }
      doc.forms = (doc.forms ?? []).filter((f) => f.id !== op.id);
      if (doc.forms.length === 0) delete doc.forms;
      return;
  }
}

export type ApplyResult =
  | { readonly ok: true; readonly candidate: unknown }
  | { readonly ok: false; readonly issues: readonly ValidationIssue[] };

/** Parses and applies a batch atomically to a copy. The candidate is NOT yet validated. */
export function applyOperations(document: CreativeDocument, operations: unknown): ApplyResult {
  const sink = new IssueSink("structure");
  const ops = array(creativeOperation, { min: 1, max: MAX_OPERATIONS_PER_BATCH }).parse(operations, "$ops", sink);
  if (ops === FAIL) return { ok: false, issues: sortIssues(sink.issues) };
  const doc = clone(document) as unknown as MutableDoc;
  const applySink = sink.forLayer("reference");
  ops.forEach((op, i) => {
    if (applySink.errorCount === 0) applyOne(doc, op, `$ops[${String(i)}]`, applySink);
  });
  return applySink.errorCount > 0 ? { ok: false, issues: sortIssues(sink.issues) } : { ok: true, candidate: doc };
}

/** Applies operations and re-validates the result with the same validator as every other path. */
export function applyAndValidate(
  document: CreativeDocument,
  operations: unknown,
  options: ValidateOptions = {}
): ValidationResult {
  const applied = applyOperations(document, operations);
  if (!applied.ok) return { ok: false, canSave: false, issues: applied.issues };
  return validateCreativeDocument(applied.candidate, options);
}
